import axios from "../../api/axiosInstance";
import { STATUS, extractErrorInfo, getProjectId } from "./sharedfunction";

/**
 * Create a new user with assigned roles, organization, and projects
 * @param {Object} params
 * @param {string} params.username - Username for the new user
 * @param {string} params.email - Email address for the new user
 * @param {string} params.password - Password for the new user
 * @param {Array<string>} params.roles - Array of role IDs to assign
 * @param {string} [params.organization] - Organization ID (nullable)
 * @param {Array<string>} [params.projects] - Array of project IDs
 * @returns {Promise<Object>} Response object with status, data/error, and message
 */
export async function createUserWithRole({ 
  username, 
  email, 
  password, 
  roles, 
  organization, 
  projects 
} = {}) {
  try {
    if (!username || username.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Username is required",
      };
    }

    if (!email || email.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Email is required",
      };
    }

    if (!roles || !Array.isArray(roles) || roles.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "At least one role is required",
      };
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/user/create`,
      { username, email, password, roles, organization, projects },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error creating user:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Change password for the currently authenticated user
 * @param {Object} params
 * @param {string} params.currentPassword - User's current password
 * @param {string} params.newPassword - New password
 * @returns {Promise<Object>} Response object with status, data/error, and message
 */
export async function changePassword({ currentPassword, newPassword } = {}) {
  try {
    if (!currentPassword || currentPassword.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Current password is required",
      };
    }

    if (!newPassword || newPassword.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "New password is required",
      };
    }

    const projectId = getProjectId();
    let url = `${process.env.REACT_APP_API_URL}/user/change-password`;
    if (projectId) {
      url += `?projectId=${projectId}`;
    }

    const response = await axios.post(
      url,
      { currentPassword, newPassword },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error changing password:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update username and/or email for the currently authenticated user
 * @param {Object} params
 * @param {string} [params.username] - New username (optional)
 * @param {string} [params.email] - New email address (optional)
 * @returns {Promise<Object>} Response object with status, data/error, and message
 */
export async function updateUserInfo({ username, email } = {}) {
  try {
    if (!username && !email) {
      return {
        status: STATUS.ERROR,
        error: "At least one field (username or email) must be provided",
      };
    }

    const payload = {};
    if (username) payload.username = username;
    if (email) payload.email = email;

    const projectId = getProjectId();
    let url = `${process.env.REACT_APP_API_URL}/user/updateInfo`;
    if (projectId) {
      url += `?projectId=${projectId}`;
    }

    const response = await axios.put(
      url,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating user info:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get all users belonging to the authenticated user's organization
 * @returns {Promise<Object>} Response object with status, data/error, and message
 */
export async function getUsersByOrganization() {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/user/getUsersByOrg`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching users by organization:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update user details including username, email, roles, organization, and projects
 * @param {Object} params
 * @param {string} params.userId - ID of the user to update
 * @param {string} [params.username] - New username (optional)
 * @param {string} [params.email] - New email address (optional)
 * @param {Array<string>} params.roles - Array with exactly one role ID
 * @param {string} [params.organization] - Organization ID (nullable)
 * @param {Array<string>} [params.projects] - Array of project IDs (optional)
 * @returns {Promise<Object>} Response object with status, data/error, and message
 */
export async function updateUserWithRole({ 
  userId, 
  username, 
  email, 
  roles, 
  organization, 
  projects 
} = {}) {
  try {
    if (!userId || userId.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "User ID is required",
      };
    }

    if (!roles || !Array.isArray(roles) || roles.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "At least one role is required",
      };
    }

    const payload = { userId, roles };
    if (username) payload.username = username;
    if (email) payload.email = email;
    if (organization !== undefined) payload.organization = organization;
    if (projects) payload.projects = projects;

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/user/update`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating user with role:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Soft delete one or more users by their IDs
 * @param {Object} params
 * @param {Array<string>} params.ids - Array of user IDs to delete
 * @returns {Promise<Object>} Response object with status, data/error, and message
 */
export async function deleteUsers({ ids } = {}) {
  try {
    if (!ids || !Array.isArray(ids) || ids.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "At least one user ID is required",
      };
    }

    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/user/delete`,
      {
        data: { ids },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error deleting users:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Request a password reset link to be sent to the user's email
 * @param {Object} params
 * @param {string} params.email - User's email address
 * @param {string} params.type - Email type for reset request
 * @returns {Promise<Object>} Response object with status, data/error, and message
 */
export async function requestPasswordReset({ email, type } = {}) {
  try {
    if (!email || email.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Email is required",
      };
    }

    if (!type || type.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Type is required",
      };
    }

    const projectId = getProjectId();

    let url = `${process.env.REACT_APP_API_URL}/user/request-reset`;
    if (projectId) {
      url += `?projectId=${projectId}`;
    }

    const response = await axios.post(
      url,
      { email, type },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error requesting password reset:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Validate reset/set-password token without consuming it.
 * @param {Object} params
 * @param {string} params.token - Reset token from email link
 * @returns {Promise<Object>} Response object with status, data/error, and message
 */
export async function validateResetToken({ token } = {}) {
  try {
    if (!token || token.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Reset token is required",
        code: "TOKEN_MISSING",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/user/validate-reset-token`,
      {
        params: { token: token.trim() },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      code: response?.data?.code,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error validating reset token:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Reset user password using the token from the reset email
 * @param {Object} params
 * @param {string} params.token - Reset token from email
 * @param {string} params.newPassword - New password
 * @returns {Promise<Object>} Response object with status, data/error, and message
 */
export async function resetPassword({
  token,
  newPassword,
  tosLegalDocumentVersionId,
  privacyLegalDocumentVersionId,
} = {}) {
  try {
    if (!token || token.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Reset token is required",
      };
    }

    if (!newPassword || newPassword.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "New password is required",
      };
    }

    const payload = { token, newPassword };
    if (tosLegalDocumentVersionId && privacyLegalDocumentVersionId) {
      payload.tos_legal_document_version_id = tosLegalDocumentVersionId;
      payload.privacy_legal_document_version_id = privacyLegalDocumentVersionId;
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/user/resetPassword`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error resetting password:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Validate if the current user session is valid and user exists
 * @returns {Promise<Object>} Response object with status, data/error, and message
 */
export async function validateUser() {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/user/validateUser`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error validating user:", error);
    return extractErrorInfo(error);
  }
}

// ============= Project-Specific User Endpoints =============

/**
 * Get all users for a specific project
 * @param {Object} params
 * @param {string} params.projectId - Project ID
 * @returns {Promise<Object>} Response object with status, data/error, and message
 */
export async function getProjectUsers({ projectId } = {}) {
  try {
    if (!projectId || projectId.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/projectuser/projectUsers`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching project users:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get all available users for adding to a project
 * @param {Object} params
 * @param {string} params.projectId - Project ID
 * @returns {Promise<Object>} Response object with status, data/error, and message
 */
export async function getAllUsersForProject({ projectId } = {}) {
  try {
    if (!projectId || projectId.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/projectuser/allUsersForProject`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching users for project:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Assign a user to a project with roles
 * @param {Object} params
 * @param {string} params.userId - User ID
 * @param {string} params.projectId - Project ID
 * @param {Array<string>} params.roleIds - Array of role IDs
 * @returns {Promise<Object>} Response object with status, data/error, and message
 */
export async function assignUserToProject({ userId, projectId, roleIds } = {}) {
  try {
    if (!userId || userId.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "User ID is required",
      };
    }

    if (!projectId || projectId.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!roleIds || !Array.isArray(roleIds) || roleIds.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "At least one role ID is required",
      };
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/projectuser/assignUserToProject`,
      { userId, roleIds },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error assigning user to project:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update user roles in a project
 * @param {Object} params
 * @param {string} params.userId - User ID
 * @param {string} params.projectId - Project ID
 * @param {Array<string>} params.roleIds - Array of role IDs
 * @returns {Promise<Object>} Response object with status, data/error, and message
 */
export async function updateUserInProject({ userId, projectId, roleIds } = {}) {
  try {
    if (!userId || userId.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "User ID is required",
      };
    }

    if (!projectId || projectId.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!roleIds || !Array.isArray(roleIds) || roleIds.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "At least one role ID is required",
      };
    }

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/${projectId}/projectuser/updateUserToProject`,
      { userId, roleIds },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating user in project:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Remove users from a project
 * @param {Object} params
 * @param {Array<string>} params.userIds - Array of user IDs to remove
 * @param {string} params.projectId - Project ID
 * @returns {Promise<Object>} Response object with status, data/error, and message
 */
export async function removeProjectUsers({ userIds, projectId } = {}) {
  try {
    if (!userIds || !Array.isArray(userIds) || userIds.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "At least one user ID is required",
      };
    }

    if (!projectId || projectId.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/${projectId}/projectuser/removeProjectUsers`,
      { userIds },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error removing project users:", error);
    return extractErrorInfo(error);
  }
}

